import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';
import { getEnv } from 'src/utils/env.vite';

import { API_URLS } from '../constants/ApiUrls';

let eosToken: string | null = null;
let isTokenFetched = false;
let tokenPromise: Promise<string | null> | null = null;

export const fetchEosToken = async (): Promise<string | null> => {
  if (isTokenFetched && eosToken) {
    return eosToken;
  }

  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      const token = sessionStorage.getItem('access_token') ?? '';
      const response = await axios.get(
        `${getEnv('VITE_APP_EOS_API_BASE_URL')}${API_URLS.EOS_TOKEN}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      eosToken = response.data ?? '';
      isTokenFetched = true;
      return eosToken;
    } catch {
      isTokenFetched = false;
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
};

export const setEosToken = (token: string | null) => {
  eosToken = token;
  isTokenFetched = !!token;
};

export const clearEosToken = () => {
  eosToken = null;
  isTokenFetched = false;
  tokenPromise = null;
};

export const getEosToken = () => eosToken;

export const initializeEosToken = async (): Promise<void> => {
  if (!isTokenFetched) {
    await fetchEosToken();
  }
};

interface EosTokenContextType {
  isTokenReady: boolean;
  token: string | null;
  error: string | null;
}

const EosTokenContext = createContext<EosTokenContextType>({
  isTokenReady: false,
  token: null,
  error: null,
});

export const useEosToken = () => {
  const context = useContext(EosTokenContext);
  if (!context) {
    throw new Error('useEosToken must be used within EosTokenProvider');
  }
  return context;
};

interface EosTokenProviderProps {
  children: ReactNode;
}

export const EosTokenProvider: React.FC<EosTokenProviderProps> = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initToken = async () => {
      try {
        await fetchEosToken();
      } catch {
        setError('Failed to initialize EOS token');
      } finally {
        setIsReady(true);
      }
    };

    initToken();
  }, []);

  const value = useMemo(
    () => ({
      isTokenReady: isReady,
      token: eosToken,
      error,
    }),
    [isReady, eosToken, error]
  );

  return (
    <EosTokenContext.Provider value={value}>
      {children}
    </EosTokenContext.Provider>
  );
};
